import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const money = (n) => `${n < 0 ? "−" : ""}£${Math.abs(n || 0).toFixed(2)}`;
const clean = (n) => (n || "").replace(/\*/g, " · ");

export function LandlordProfitSection({ client }) {
  const [rows, setRows] = useState([]);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    client().get("/landlord/profit-statements").then((r) => setRows(r.data || [])).catch(() => {});
    client().get("/landlord/occupancy-trend").then((r) => setTrend(r.data || [])).catch(() => {});
  }, [client]);
  const download = async (r) => {
    const res = await client().get(`/landlord/profit-statements/${r.property_id}/pdf`, { params: { month: r.month }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `profit-statement-${r.month}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      {trend.map((p) => (
        <div key={p.property_id} className="mt-8 ns-card p-5" data-testid={`landlord-occupancy-trend-${p.property_id}`}>
          <div className="font-serif text-xl">{clean(p.property_name)}</div>
          <div className="text-xs text-slate-500 mb-2">Occupancy (bars) and revenue per available night (line), last 12 months. Amber = quiet month.</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={p.months} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(m) => m.slice(2)} />
                <YAxis yAxisId="occ" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                <YAxis yAxisId="rev" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, n) => (n === "occupancy_pct" ? [`${v}%`, "Occupancy"] : [`£${v}`, "Revenue / night"])} />
                <Bar yAxisId="occ" dataKey="occupancy_pct" radius={[4, 4, 0, 0]}>
                  {p.months.map((m) => <Cell key={m.month} fill={m.quiet ? "#f59e0b" : "#0f766e"} />)}
                </Bar>
                <Line yAxisId="rev" type="monotone" dataKey="revpan" stroke="#1e293b" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      ))}
      <div className="mt-8 ns-card p-5" data-testid="landlord-profit-statements">
        <div className="font-serif text-xl mb-1">Profit statements</div>
        <div className="text-xs text-slate-500 mb-3">Download any month's income, costs and profit as a PDF.</div>
        {rows.length === 0 && <div className="text-sm text-slate-400 italic">No statements yet.</div>}
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.property_id}-${r.month}`} className="border-t border-stone-100" data-testid={`landlord-statement-row-${r.property_id}-${r.month}`}>
                <td className="py-2">{r.month}</td>
                <td className="text-slate-600 truncate max-w-[220px]">{clean(r.property_name)}</td>
                <td className="text-right font-mono">{money(r.income)}</td>
                <td className="text-right font-mono">{money(-r.expenses)}</td>
                <td className={`text-right font-mono ${r.net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(r.net)}</td>
                <td className="text-right">
                  <button onClick={() => download(r)} className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 transition-colors"
                    data-testid={`landlord-statement-download-${r.property_id}-${r.month}`}><Download className="w-3.5 h-3.5" /> PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
